<template>
	<div class="filter-mobile">
		<FilterContainer :with-divider="false">
			<div v-if="filters.length > 0">
				<FilterTagList
					:emit-analytics="false"
					:show-reset-in-header="true"
					:is-modal="true"
				/>
			</div>
		</FilterContainer>
		<ListFilters
			:filters
			:has-filters="filters.length > 0"
			:list-type="listType"
			:destination-label="destinationLabel"
		/>
	</div>
</template>

<script lang="ts" setup>
import FilterTagList from '@lmt-rpb/FilterTagList/FilterTagList.vue';
import ListFilters from '@lmt-rpb/ListFilters/ListFilters.vue';
import FilterContainer from '@lmt-rpb/FilterContainer/FilterContainer.vue';
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { getOS } from '@/js/utils/environmentUtils';
import { useFilters } from 'src/composables/useFIlters';

type Props = {
	listType: 'hotel' | 'region' | 'offer';
	destinationLabel?: string;
};

const { activeFilters: filters } = useFilters();

defineProps<Props>();

const selector = 'dialog.search-modal[open]';
const filterTagListSelector = '.filter-mobile > .filter-container';
const lastKnownHeight = ref(0);
let observer: MutationObserver | null = null;

function getHeight(): number {
	const element = document.querySelector(filterTagListSelector);
	if (!element) return 0;

	const computedStyle = getComputedStyle(element);
	const marginTop = parseFloat(computedStyle.marginTop) || 0;
	const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

	// Dynamically include margin changes in the height calculation
	return element.clientHeight + marginTop + marginBottom;
}

function adjustScroll(delta: number) {
	const { isiOS } = getOS();
	if (!isiOS) {
		return;
	}
	const scrollContainer = document.querySelector(selector + ' .search-modal__main');
	if (scrollContainer) {
		scrollContainer.scrollTop += delta;
	}
}

onMounted(() => {
	nextTick(() => {
		const targetNode = document.querySelector(filterTagListSelector);
		if (targetNode) {
			lastKnownHeight.value = getHeight();

			observer = new MutationObserver(() => {
				const newHeight = getHeight();
				const delta = newHeight - lastKnownHeight.value;

				// Adjust scroll for both height increase and decrease
				if (delta !== 0) {
					adjustScroll(delta);
				}

				lastKnownHeight.value = newHeight;
			});

			observer.observe(targetNode, { childList: true, subtree: true, attributes: true });
		}
	});
});

onBeforeUnmount(() => {
	if (observer) {
		observer.disconnect();
		observer = null;
	}
});
</script>

<style lang="scss" scoped>
$common-filter-border: 0.1rem solid $color-primary-l4;

.filter-tag-list {
	margin-bottom: 0;
	margin-top: 2.4rem;
}

.filter-mobile {
	padding: 0;
	max-width: 144rem;
	width: 100%;
	position: relative;
	display: flex;
	flex-flow: column;
	align-items: center;

	:deep(.filter-container:nth-child(1)) {
		margin-top: 0;
		margin-bottom: 2.4rem;
	}

	@media (min-width: $breakpoint-extralarge) {
		display: none;
	}
}
</style>
